import { ExploreOutletContext } from '@/components/layouts/Layout';
import { CANONICAL_LABELS, getCollectionLabel } from '@/lib/content-labels';
import type { CollectionKind, ContentType } from '@vivport/backend/types';

export type ContentTypeValue = ContentType;
export type LayoutScreen = NonNullable<ExploreOutletContext['screen']>['screen'];

// An allow-list, not a deny-list: a content type added to the backend enum must be opted
// IN to editing rather than becoming editable by default. Both the predicate's runtime
// check and its type derive from this one array, so they cannot drift apart.
const EDITABLE_CONTENT_TYPES = [
  'ANCHOR',
  'EVENT',
  'DOCUMENT',
  'PHOTO',
  'RECORDING',
  'VIDEO',
  'FILE',
  'URL',
  'NEO_CHAT',
  'TEMPLATE',
] as const satisfies readonly ContentTypeValue[];

export type EditableContentType = (typeof EDITABLE_CONTENT_TYPES)[number];

export const SCREEN_BY_CONTENT_TYPE: Partial<Record<ContentTypeValue, LayoutScreen>> = {
  ANCHOR: 'ANCHOR',
  DOCUMENT: 'DOCUMENT',
  EVENT: 'EVENT',
  PHOTO: 'photo',
  RECORDING: 'recording',
  VIDEO: 'video',
};

export const STATIC_TITLE_BY_CONTENT_TYPE: Partial<Record<ContentTypeValue, string>> = {
  PHOTO: 'Photo',
  RECORDING: 'Recording',
  VIDEO: 'Video',
  FILE: 'File',
  URL: 'URL',
  NEO_CHAT: 'Neo Chat',
  MESSAGE: 'Message',
  PROCESS_RUN: 'Process Run',
  MANUAL_SESSION: 'Manual Session',
  COLLECTION: 'Collection',
  TEMPLATE: 'Template',
};

export function normalizeThreadSubject(subject: string | null | undefined): string | null {
  if (!subject || subject.trim().length === 0) {
    return null;
  }
  const trimmed = subject.trim();
  const withoutReplyPrefixes = trimmed.replace(/^(\s*(re|fw|fwd)\s*:\s*)+/i, '').trim();
  return withoutReplyPrefixes.length > 0 ? withoutReplyPrefixes : trimmed;
}

export function resolveMessageThreadTitleFromDetail(content: {
  title?: string | null;
  conversation?: { items?: Array<{ subject?: string | null }> } | null;
}): string | null {
  const conversationItems = content.conversation?.items ?? [];
  for (const item of conversationItems) {
    const normalized = normalizeThreadSubject(item.subject ?? null);
    if (normalized) {
      return normalized;
    }
  }
  return normalizeThreadSubject(content.title ?? null);
}

export function getScreenForContentType(contentType: ContentTypeValue): LayoutScreen | undefined {
  return SCREEN_BY_CONTENT_TYPE[contentType];
}

export function isEditableContentType(contentType: ContentTypeValue): contentType is EditableContentType {
  return (EDITABLE_CONTENT_TYPES as readonly string[]).includes(contentType);
}

export function getDefaultTitleForContentType(
  contentType: ContentTypeValue,
  contentCode: string,
  collectionKind?: CollectionKind | null
): string {
  if (contentType === 'ANCHOR') return `${CANONICAL_LABELS.anchor} ${contentCode}`;
  if (contentType === 'EVENT') return `${CANONICAL_LABELS.event} ${contentCode}`;
  if (contentType === 'DOCUMENT') return `${CANONICAL_LABELS.document} ${contentCode}`;
  if (contentType === 'COLLECTION') return getCollectionLabel(collectionKind);
  return STATIC_TITLE_BY_CONTENT_TYPE[contentType] ?? 'Content';
}
